import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormControl, FormHelperText, InputAdornment, TextField } from "@mui/material"
import { checkTouched, isTouched as hasBeenTouched } from "form/tools/validation"
import { Field, FieldProps } from "formik"
import React from "react"
import { NumberInput } from "./base/NumberInput"

interface IFieldTextProps {
  fieldName: string // ex: "main.order_id"
  label: string
  isNumber?: boolean
  disabled?: boolean
  isReadOnly?: boolean
  required?: boolean
  autoFocus?: boolean
  value: string | number | null
  endAdornmentText?: string
  textarea?: boolean
  roundValue?: boolean
  error?: string
  sx?: any
  isPassword?: boolean
  clearable?: boolean
  refreshValue?: number
  beforeChange?(value: any): any
}

export const FieldText = React.memo(
  ({
    fieldName,
    label,
    isNumber,
    disabled,
    required,
    isReadOnly,
    isPassword,
    endAdornmentText,
    value,
    error,
    sx,
    clearable,
    textarea,
    roundValue,
    autoFocus,
    refreshValue,
    beforeChange
  }: IFieldTextProps) => {
    const [val, setVal] = React.useState(typeof value === "number" ? Math.round(value) : value)
    // used to trigger a refresh of the value, when we cancel changes by instance
    React.useEffect(() => {
      setVal(value)
    }, [refreshValue])
    const timeout = React.useRef<any>(undefined)
    return (
      <Field name={fieldName}>
        {({ form: { touched, setFieldValue } }: FieldProps) => {
          const isTouched: boolean = hasBeenTouched(touched, fieldName)
          const isDisabled: boolean = disabled || Boolean(isReadOnly)
          const hasError = isTouched ? error !== undefined && error !== "" : false
          const displayClearButton = clearable && value !== null && !disabled
          return (
            <FormControl
              id={fieldName}
              error={hasError}
              sx={{
                "position": "relative",
                "& .icon": {
                  position: "absolute",
                  top: ".75rem",
                  right: ".6rem",
                  cursor: "pointer",
                  color: theme => theme.palette.text.secondary
                },
                "& .icon:hover": {
                  color: theme => theme.palette.text.primary
                },
                "& input": { paddingRight: clearable ? "1.8rem" : undefined },
                ...sx
              }}>
              <TextField
                id={`${fieldName}-text-field`}
                name={fieldName}
                variant="outlined"
                error={hasError}
                required={required}
                label={label}
                size="small"
                autoFocus={autoFocus}
                value={val === null ? "" : val}
                multiline={textarea}
                rows={textarea ? 4 : undefined}
                type={isNumber ? undefined : isPassword ? "password" : "text"}
                onChange={event => {
                  clearTimeout(timeout.current)
                  checkTouched(touched, fieldName)
                  let newValue = isNumber
                    ? event.target.value !== ""
                      ? Number(event.target.value)
                      : null
                    : event.target.value
                  if (roundValue && typeof newValue === "number") {
                    newValue = Math.round(newValue)
                  }
                  setVal(newValue)
                  if (beforeChange) {
                    newValue = beforeChange(newValue)
                    if (roundValue && typeof newValue === "number") {
                      newValue = Math.round(newValue)
                    }
                  }
                  timeout.current = setTimeout(() => setFieldValue(fieldName, newValue), 500)
                }}
                onBlur={() => {
                  if (!isTouched) {
                    checkTouched(touched, fieldName)
                    setFieldValue(fieldName, val)
                  }
                }}
                InputProps={{
                  endAdornment: endAdornmentText && (
                    <InputAdornment position="end" sx={{ pr: displayClearButton ? ".5rem" : 0 }}>
                      {endAdornmentText}
                    </InputAdornment>
                  ),
                  inputComponent: isNumber ? (NumberInput as any) : undefined
                }}
                disabled={isDisabled}
              />
              {hasError && <FormHelperText id={`${fieldName}-error`}>{error}</FormHelperText>}
              {displayClearButton && (
                <FontAwesomeIcon
                  id={`${fieldName}-clear-button`}
                  icon={faXmark}
                  className="icon"
                  onClick={() => {
                    setVal(null)
                    checkTouched(touched, fieldName)
                    setFieldValue(fieldName, null)
                  }}
                />
              )}
            </FormControl>
          )
        }}
      </Field>
    )
  }
)
